
import { Trip, TripLog } from '@/generated-clients/myTrips';
import { RouterQuery } from '@/router/Routes';
import { deepEquals } from '@/utils/copy';
import { LoadState } from '@/utils/types';
import { Statistics } from '@clients/bff';
import AppContent from '@components/new-styleguide/AppContent.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppVehicleHeader from '@components/new-styleguide/AppVehicleHeader.vue';
import TripStatisticBar from '@components/new-styleguide/TripStatisticBar.vue';
import DeviceHelpSection from '@components/shared/section/DeviceHelpSection.vue';
import NoTripsSection from '@components/shared/section/NoTripsSection.vue';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import StatisticFilterModal, { StatisticFiltersModel } from '@components/statistic/StatisticFilterModal.vue';
import StatisticHeatmap from '@components/statistic/StatisticHeatmap.vue';
import StatisticTabsView from '@components/statistic/StatisticTabsView.vue';
import SvgGradientDefinitions from '@components/statistic/SvgGradientDefinitions.vue';
import { calculateFromAndUntilDates, DatePeriod, isoDateToDateString } from '@util/dates-and-times/period';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Dictionary } from 'vue-router/types/router';
import { namespace } from 'vuex-class';

const TriplogModule = namespace('Triplog');
const StatisticModule = namespace('Statistic');
const RouterModule = namespace('Router');

@Component({
  name: 'myTripsStatistic',
  components: {
    DeviceHelpSection,
    NoTripsSection,
    AppPage,
    AppContent,
    AppVehicleHeader,
    StatisticHeatmap,
    TripStatisticBar,
    StatisticTabsView,
    SvgGradientDefinitions,
    StatisticFilterModal,
    InfoBoxWarning,
  },
})
export default class Statistic extends Vue {
  @StatisticModule.Action('fetchStatistics')
  private fetchStatistics;

  @StatisticModule.Getter('getStatistics')
  private statistics: Statistics;

  @StatisticModule.Getter('getStatisticsFilters')
  private statisticsFilter;

  @StatisticModule.Getter('getStatisticsStatus')
  private statisticsStatus: LoadState;

  @TriplogModule.Getter('getVehiclesStatus')
  private vehiclesStatus;

  @TriplogModule.Getter('getVehicles')
  private vehicles: TripLog[];

  @TriplogModule.Getter('getSelectedTriplog')
  private triplog: TripLog;

  @RouterModule.State('query')
  private query: RouterQuery;

  @TriplogModule.Action('fetchLatestTrip')
  private fetchLatestTrip;

  @TriplogModule.Getter('getLatestTrip')
  private latestTrip: Trip;

  @TriplogModule.Getter('getLatestTripStatus')
  private latestTripStatus: LoadState;

  private selectedTab = 0;

  private filters: StatisticFiltersModel = null;
  private filtersOpen: boolean = false;

  async created() {
    let datePeriod: DatePeriod;
    if (this.$route.query.datePeriod && DatePeriod[this.$route.query.datePeriod as string]) {
      datePeriod = DatePeriod[this.$route.query.datePeriod as string];
    } else {
      datePeriod = this.statisticsFilter.datePeriod || DatePeriod.LAST_90_DAYS_LAST_TRIP;
    }

    let from: string;
    let until: string;
    if (datePeriod === DatePeriod.CUSTOM) {
      from = this.$route.query.from || this.statisticsFilter.from;
      until = this.$route.query.until || this.statisticsFilter.until;
    } else {
      const periodDates = calculateFromAndUntilDates(datePeriod, new Date());
      from = periodDates.from;
      until = periodDates.until;
    }

    this.filters = { datePeriod, from, until };

    if (this.vehiclesStatus === LoadState.Success) {
      this.handleTriplogInput();
    }
  }

  get fullscreenMap() {
    return this.query.fullscreenMap === 'true';
  }

  get backLocation() {
    if (this.fullscreenMap) {
      return { query: { ...this.query, fullscreenMap: undefined } };
    }
  }

  private openFilters() {
    this.filtersOpen = true;
  }

  get isLoading() {
    return this.statisticsStatus === LoadState.Loading || this.statisticsStatus === LoadState.None || this.vehiclesStatus === LoadState.Loading || !this.filters;
  }

  get errorLoading() {
    return this.statisticsStatus === LoadState.Error || this.vehiclesStatus === LoadState.Error;
  }

  get showSmartConnectInfo() {
    return this.triplog ? this.triplog.smartConnected : this.vehicles.some(v => v.smartConnected);
  }

  get summary() {
    return this.statistics.statisticTabs.keyData.summary;
  }

  get smartConnected() {
    return this.triplog && this.triplog.smartConnected;
  }

  get showNoTripsSection() {
    return this.latestTripStatus === LoadState.Success && !this.latestTrip;
  }

  private handleTriplogInput() {
    this.refetchStatistics()
    this.fetchLatestTrip({ vehicleId: this.triplog.vehicle.id });
  }

  private submitFilters(filters: StatisticFiltersModel) {
    this.filters = filters;
    this.refetchStatistics();
    this.filtersOpen = false;
  }

  private refetchStatistics() {
    this.updateFilters();
    this.fetchStatistics({ ...this.filters, vehicleId: this.triplog && this.triplog.vehicle.id, smartConnectInfo: this.showSmartConnectInfo });
    this.updateQueryParams();
  }

  private updateFilters() {
    if (this.filters.datePeriod !== DatePeriod.CUSTOM) {
      const lastTripDate = this.triplog?.lastTripTimestamp ? new Date(this.triplog.lastTripTimestamp) : new Date();
      const periodDates = calculateFromAndUntilDates(this.filters.datePeriod, lastTripDate);

      this.filters = { ...this.filters, ...periodDates };
    }
  }

  private updateQueryParams() {
    let query: Dictionary<string> = {
      ...this.query,
      datePeriod: this.filters.datePeriod
    };

    if (this.filters.datePeriod === DatePeriod.CUSTOM) {
      query.from = isoDateToDateString(this.filters.from);
      query.until = isoDateToDateString(this.filters.until);
    } else {
      query.from = undefined;
      query.until = undefined;
    }

    if (!deepEquals(query, this.$route.query)) {
      this.$router.replace({ query });
    }
  }
}
